"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { getClientRegion, getUserRegionSlug } from "@/services/region";
import { readCachedSiteSettings } from "@/services/settingsClient";
import type { SiteSettings } from "@/services/settingsShared";
import { useSiteSettingsSeed } from "@/components/SiteSettingsProvider";
import { useOptionalCurrentUser } from "@/modules/auth/context/CurrentUserContext";

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener("site-settings-updated", onStoreChange);

  return () => window.removeEventListener("site-settings-updated", onStoreChange);
}

/**
 * The current region's site settings.
 *
 * Starts from the seed the root layout took off the server, so the first render
 * already has the region's logo and palette — no post-hydration swap.
 *
 * Then it subscribes to the client cache, which is what keeps a region switch
 * honest: proxy.ts rewrites /eg/en to /en, so the layout (and therefore the
 * seed) survives the navigation and would otherwise keep serving the region the
 * user just left. The cache is keyed on locale AND region, so asking it for the
 * new region returns nothing and triggers a refetch; the resulting event is
 * what re-renders every consumer.
 */
export function useSiteSettings(): SiteSettings | null {
  const seed = useSiteSettingsSeed();
  const locale = useLocale();
  const pathname = usePathname();
  const currentUser = useOptionalCurrentUser();
  const urlRegion = getClientRegion(pathname ?? "");

  // The URL is the source of truth, with the signed-in user's own region as the
  // fallback when it carries no region segment.
  //
  // Only a *full* page load is guaranteed to have one: the server reads the
  // `x-region` header proxy.ts sets, but on the client the region is read back
  // off the pathname, and a client-side navigation to a bare `/{locale}/...`
  // target (a post-payment redirect, say) drops it. Without this fallback that
  // navigation asks /api/settings for no region at all, which answers with the
  // Global payload — the Global logo, name and palette land on a user who
  // belongs to a region, and stay until the next full refresh.
  const region = urlRegion ?? getUserRegionSlug(currentUser?.user);

  // While the URL has no region AND the user is still loading, it isn't known
  // yet whether this is a Global visitor or a regional user mid-navigation.
  // Revalidating now would fetch and broadcast the Global payload to every
  // consumer only to replace it a moment later — a visible flash of the wrong
  // brand. The seed the server already resolved covers this gap.
  const canRevalidate = urlRegion !== null || !currentUser?.loading;

  // Both branches return a stable reference (the cached object, or the seed
  // prop), which is what useSyncExternalStore requires of a snapshot.
  const getSnapshot = useCallback((): SiteSettings | null => {
    if (typeof window === "undefined") {
      return seed;
    }

    const cached = window.__SITE_SETTINGS__;
    const matchesCurrentScope =
      window.__SITE_SETTINGS_LOCALE__ === locale && window.__SITE_SETTINGS_REGION__ === region;

    return cached && matchesCurrentScope ? cached : seed;
  }, [seed, locale, region]);

  const getServerSnapshot = useCallback((): SiteSettings | null => seed, [seed]);

  const settings = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Revalidation only. readCachedSiteSettings refreshes in the background when
  // the cache is missing, stale, or for another region; the result arrives
  // through the subscription above, so nothing here sets state.
  useEffect(() => {
    if (!canRevalidate) {
      return;
    }

    readCachedSiteSettings(locale, region);
  }, [locale, region, canRevalidate]);

  return settings;
}
